import type {
  InstagramTotals,
  SocialDateRange,
  SocialMetricSeries,
} from '../../types/socialReporting.js';

// Organic Social Reporting — pure series helpers.
//
// No I/O, no Date.now (dates are injected). The server fetches Zernio's insights
// envelopes; everything about how a metric envelope is READ, how a daily series is
// SUMMED within a window, and how the FB/IG follower lines MERGE lives here so it
// can be unit-tested off the observed fixtures (observed-2026-07-22).
//
// ═══ THE ENVELOPE SHAPE (observed 2026-07-22) ═══
// getFacebookPageInsights / getInstagramAccountInsights / getInstagramFollowerHistory
// all return:
//   { success, accountId, platform, dateRange:{since,until}, metricType,
//     metrics: { <name>: { total, values:[{date,value}] } }, dataDelay }
// For metricType='total_value' the `values` array is empty (totals only); for
// 'time_series' it carries daily points. A metric the platform never returned is
// simply ABSENT from `metrics` (an index-signature map), which is distinct from a
// metric that returned all-zero — the null-vs-zero rule this whole surface turns on.

// ---------------------------------------------------------------------------
// tolerant readers
// ---------------------------------------------------------------------------

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Read one metric out of an insights envelope's `metrics` map into a series.
 *
 * Returns null when the metric is ABSENT — never a zeroed series — because a
 * missing metric and a genuinely-zero metric are different claims (a synthesized
 * metric like followers_gained can come back with no history at all). A present
 * metric with no daily points still returns a series (its `total` is real, from a
 * total_value response).
 */
export function readMetricSeries(envelope: unknown, name: string): SocialMetricSeries | null {
  if (!envelope || typeof envelope !== 'object') return null;
  const metrics = (envelope as { metrics?: unknown }).metrics;
  if (!metrics || typeof metrics !== 'object') return null;
  const block = (metrics as Record<string, unknown>)[name];
  if (!block || typeof block !== 'object') return null;

  const b = block as { total?: unknown; values?: unknown };
  const values: Array<{ date: string; value: number }> = [];
  if (Array.isArray(b.values)) {
    for (const raw of b.values) {
      if (!raw || typeof raw !== 'object') continue;
      const date = str((raw as { date?: unknown }).date);
      const value = num((raw as { value?: unknown }).value);
      if (date !== null && value !== null) values.push({ date, value });
    }
  }
  return { total: num(b.total) ?? 0, values };
}

/** Read a single total (total_value) metric — the number, or null if absent. */
export function readMetricTotal(envelope: unknown, name: string): number | null {
  const series = readMetricSeries(envelope, name);
  return series ? series.total : null;
}

/**
 * Read an IG account-insights total_value envelope into the five period totals we
 * report. Zernio's metric keys are snake_case here (accounts_engaged,
 * total_interactions, follows_and_unfollows). Any absent metric stays null.
 */
export function readInstagramTotals(envelope: unknown): InstagramTotals {
  return {
    reach: readMetricTotal(envelope, 'reach'),
    views: readMetricTotal(envelope, 'views'),
    accountsEngaged: readMetricTotal(envelope, 'accounts_engaged'),
    totalInteractions: readMetricTotal(envelope, 'total_interactions'),
    followsAndUnfollows: readMetricTotal(envelope, 'follows_and_unfollows'),
  };
}

// ---------------------------------------------------------------------------
// windowed aggregation
// ---------------------------------------------------------------------------

/** Inclusive date-string membership. Points are 'YYYY-MM-DD', so lexical compare works. */
function inRange(date: string, range: SocialDateRange): boolean {
  return date >= range.from && date <= range.to;
}

/** Sum a daily series' values that fall inside the window (inclusive). */
export function sumSeriesInRange(series: SocialMetricSeries | null, range: SocialDateRange): number {
  if (!series) return 0;
  let sum = 0;
  for (const p of series.values) if (inRange(p.date, range)) sum += p.value;
  return sum;
}

/** The number of distinct daily points inside a window — for "has any data" checks. */
export function countPointsInRange(series: SocialMetricSeries | null, range: SocialDateRange): number {
  if (!series) return 0;
  let n = 0;
  for (const p of series.values) if (inRange(p.date, range)) n += 1;
  return n;
}

/**
 * A running-count metric's net change across a window: last point minus first
 * point (both inside the window). Used as the follower-growth fallback when the
 * synthesized gained/lost series is empty. Returns null when fewer than one point
 * lands in range (nothing to difference).
 */
export function runningCountDelta(
  series: SocialMetricSeries | null,
  range: SocialDateRange,
): number | null {
  if (!series) return null;
  const pts = series.values
    .filter((p) => inRange(p.date, range))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (pts.length === 0) return null;
  if (pts.length === 1) return 0;
  return pts[pts.length - 1]!.value - pts[0]!.value;
}

/** The latest running-count value inside a window (e.g. current follower count). */
export function latestInRange(
  series: SocialMetricSeries | null,
  range: SocialDateRange,
): number | null {
  if (!series) return null;
  let best: { date: string; value: number } | null = null;
  for (const p of series.values) {
    if (!inRange(p.date, range)) continue;
    if (!best || p.date > best.date) best = p;
  }
  return best ? best.value : null;
}

// ---------------------------------------------------------------------------
// follower series merge (for the trend chart)
// ---------------------------------------------------------------------------

export interface FollowerTrendRow {
  date: string;
  facebook: number | null;
  instagram: number | null;
}

/**
 * Merge FB `page_follows` and IG `follower_count` daily series into one row set
 * keyed by date, restricted to the window. A platform with no point on a given
 * day is null there (a gap in the line), never 0 — 0 followers would be a claim,
 * a missing snapshot is not. Rows are date-sorted for the chart's x-axis.
 */
export function mergeFollowerSeries(
  facebook: SocialMetricSeries | null,
  instagram: SocialMetricSeries | null,
  range: SocialDateRange,
): FollowerTrendRow[] {
  const byDate = new Map<string, FollowerTrendRow>();
  const put = (
    series: SocialMetricSeries | null,
    key: 'facebook' | 'instagram',
  ): void => {
    if (!series) return;
    for (const p of series.values) {
      if (!inRange(p.date, range)) continue;
      const row = byDate.get(p.date) ?? { date: p.date, facebook: null, instagram: null };
      row[key] = p.value;
      byDate.set(p.date, row);
    }
  };
  put(facebook, 'facebook');
  put(instagram, 'instagram');
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

// ---------------------------------------------------------------------------
// range math + IG follower-history windowing (page-and-stitch)
// ---------------------------------------------------------------------------

// The IG follower-history endpoint returns at most this many days PER QUERY — a
// per-query WINDOW cap, NOT a retention limit. Zernio stores daily follower counts
// indefinitely (its own cross-platform snapshotter), so a 12+-month trend is
// fetched by paging consecutive ≤89-day windows and stitching them.
export const IG_FOLLOWER_MAX_DAYS = 89;

/** Inclusive day count of a YYYY-MM-DD range (from==to → 1). */
export function rangeLengthDays(range: SocialDateRange): number {
  const from = Date.parse(`${range.from}T00:00:00Z`);
  const to = Date.parse(`${range.to}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / 86_400_000) + 1;
}

/**
 * Split a window into consecutive, NON-overlapping sub-windows of at most
 * `maxDays` each, in chronological order, together covering [from, to]. Used to
 * page the IG follower-history endpoint's 89-day per-query window across a longer
 * selected period. A range that already fits returns a single window; an invalid
 * range returns [].
 */
export function splitRangeIntoWindows(
  range: SocialDateRange,
  maxDays: number,
): SocialDateRange[] {
  const fromMs = Date.parse(`${range.from}T00:00:00Z`);
  const toMs = Date.parse(`${range.to}T00:00:00Z`);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs < fromMs || maxDays < 1) return [];
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const windows: SocialDateRange[] = [];
  let startMs = fromMs;
  while (startMs <= toMs) {
    const endMs = Math.min(startMs + (maxDays - 1) * 86_400_000, toMs);
    windows.push({ from: iso(startMs), to: iso(endMs) });
    startMs = endMs + 86_400_000; // next window starts the day after
  }
  return windows;
}

/**
 * Stitch multiple daily series (the per-window follower-history results) into one:
 * dedupe points by date (keep-last, so overlapping boundary days collapse),
 * chronological order, total = sum of the deduped values. Returns null when every
 * input series is null (nothing fetched). For a running-count metric (follower
 * count) consumers read `values`, not `total`.
 */
export function stitchSeries(
  seriesList: Array<SocialMetricSeries | null>,
): SocialMetricSeries | null {
  const present = seriesList.filter((s): s is SocialMetricSeries => s !== null);
  if (present.length === 0) return null;
  const byDate = new Map<string, number>();
  for (const s of present) for (const p of s.values) byDate.set(p.date, p.value);
  const values = [...byDate.entries()]
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));
  return { total: values.reduce((sum, p) => sum + p.value, 0), values };
}
